/**
 * PhaserModule — four cascaded all-pass filters whose cutoff is swept
 * by an internal LFO, creating the classic swirly peak-and-notch
 * shifting sound.
 *
 * Graph:
 *
 *   input ─┬─ dryGain ─────────────────────────────────── outputMix ─►
 *          └─► ap1 ─► ap2 ─► ap3 ─► ap4 ─┬─► wetGain ─── outputMix
 *                                        └─► feedbackGain ─► ap1
 *   lfo ─► lfoGain ─► each ap.frequency   (AudioParam sum)
 *
 * Four stages is the sweet spot — fewer sounds thin, more adds CPU
 * without much audible improvement at typical sweep rates.
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

const STAGES = 4;
/** All-pass center frequency (Hz) around which the LFO sweeps. */
const CENTER_HZ = 800;
/** Max ±swing from center at depth=1 (Hz). */
const MAX_SWING_HZ = 700;

export class PhaserModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'phaser' as const;
  readonly displayName = 'PHASER';
  readonly id: string;
  bypass = false;

  readonly rate = new NumericParameter({
    name: 'Rate',
    default: 0.4,
    min: 0.05,
    max: 8,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    formatter: (v) => (v < 1 ? v.toFixed(2) : v.toFixed(1)),
  });
  readonly depth = new NumericParameter({
    name: 'Depth',
    default: 0.6,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly feedback = new NumericParameter({
    name: 'Feedback',
    default: 0.4,
    min: 0,
    max: 0.9,
    curve: 'linear',
    smoothingMs: 20,
    randomSkew: 0.6,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 0.5,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    randomSkew: 0.7,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.rate,
    this.depth,
    this.feedback,
    this.mix,
  ];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private allPasses: BiquadFilterNode[] = [];
  private feedbackGain: GainNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private lfoOsc: OscillatorNode | null = null;
  private lfoGain: GainNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('phaser');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();
    this.feedbackGain = ctx.createGain();
    this.feedbackGain.gain.value = this.feedback.value;

    // Build the all-pass chain.
    this.allPasses = [];
    for (let i = 0; i < STAGES; i++) {
      const ap = ctx.createBiquadFilter();
      ap.type = 'allpass';
      ap.frequency.value = CENTER_HZ;
      ap.Q.value = 0.5;
      this.allPasses.push(ap);
    }
    for (let i = 0; i < STAGES - 1; i++) {
      this.allPasses[i]!.connect(this.allPasses[i + 1]!);
    }
    const last = this.allPasses[STAGES - 1]!;

    // Dry path.
    this.inputNode.connect(this.dryGain).connect(this.outputMixNode);

    // Wet path with feedback loop back to the first stage.
    this.inputNode.connect(this.allPasses[0]!);
    last.connect(this.feedbackGain);
    this.feedbackGain.connect(this.allPasses[0]!);
    last.connect(this.wetGain).connect(this.outputMixNode);

    // LFO sweeps every stage's frequency AudioParam in lockstep.
    this.lfoOsc = ctx.createOscillator();
    this.lfoOsc.type = 'sine';
    this.lfoOsc.frequency.value = this.rate.value;
    this.lfoGain = ctx.createGain();
    this.lfoGain.gain.value = this.depth.value * MAX_SWING_HZ;
    this.lfoOsc.connect(this.lfoGain);
    for (const ap of this.allPasses) this.lfoGain.connect(ap.frequency);
    this.lfoOsc.start();

    this.rate.bindAudioParam(this.lfoOsc.frequency, ctx);
    this.feedback.bindAudioParam(this.feedbackGain.gain, ctx);
    this.unsubs.push(
      this.depth.subscribe((v) => {
        if (!this.lfoGain || !this.engine) return;
        const ctx2 = this.engine.context;
        const t = ctx2.currentTime;
        this.lfoGain.gain.cancelScheduledValues(t);
        this.lfoGain.gain.setTargetAtTime(v * MAX_SWING_HZ, t, 0.02);
      }),
    );

    this.applyMix(this.mix.value, true);
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));

    this.started = true;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    try {
      this.lfoOsc?.stop();
    } catch {
      /* already stopped */
    }
    this.started = false;
  }

  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Rate') return this.lfoOsc?.frequency ?? null;
    if (paramName === 'Feedback') return this.feedbackGain?.gain ?? null;
    return null;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    readParamState(this.parameters, state.params, 'preset');
  }

  private applyMix(mix: number, immediate = false): void {
    if (!this.dryGain || !this.wetGain || !this.engine) return;
    const ctx = this.engine.context;
    const t = ctx.currentTime;
    const dry = 1 - mix;
    const wet = mix;
    if (immediate) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.01);
      this.wetGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.01);
    }
  }
}
